/*
 * Copyright (c) 2023 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, March 24th 2023, 3:43:41 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import Typography from "@mui/material/Typography";
import { useTranslatedText } from "core";
import { getDefaultDesignLayout } from "lib/factories";
import { DesignLayout, ProcessDefinition } from "lib/schemas";
import { DesignLayoutEditor } from "service-builder/components/layouts/DesignLayoutEditor";
import { OSActionButton, EmptyStateActionWrapper, Image, EmphasisText } from "ui-widgets";
import { ListLayoutEditor } from "./ListLayoutEditor";

interface InstanceListLayoutProps {
  onSaveListLayout: (layout: DesignLayout) => void;
  processDefinition: ProcessDefinition;
}

export const InstanceListLayout = ({ onSaveListLayout, processDefinition }: InstanceListLayoutProps) => {
  const { layoutId, hasCustomLayout, newDesignLayoutDefault, autoSaveNewLayout, onClickAddCustomLayout } =
    useInstanceListLayout(processDefinition);

  const LANG = useTranslatedText();

  return (
    <>
      {hasCustomLayout ? (
        <DesignLayoutEditor
          layoutId={layoutId}
          newDesignLayoutDefault={newDesignLayoutDefault}
          onSaveDesignLayout={onSaveListLayout}
          autoSaveNewLayout={autoSaveNewLayout}
          editor="CUSTOM"
          CustomEditorComponent={ListLayoutEditor}
        />
      ) : (
        <NoCustomLayoutCTA>
          <OSActionButton variant="contained" onClick={onClickAddCustomLayout} size="large">
            {LANG.addCustomListLayout}
          </OSActionButton>
        </NoCustomLayoutCTA>
      )}
    </>
  );
};

const useInstanceListLayout = (processDefinition: ProcessDefinition) => {
  const newDesignLayoutDefault = getDefaultDesignLayout({
    layout_type: "list_display",
    organizationId: processDefinition.artifact_org,
  });

  const [autoSaveNewLayout, setAutoSaveNewLayout] = React.useState(false);

  const onClickAddCustomLayout = () => setAutoSaveNewLayout(true);

  const hasCustomLayout = Boolean(processDefinition.list_view_layout) || autoSaveNewLayout;

  return {
    layoutId: processDefinition.list_view_layout,
    hasCustomLayout,
    autoSaveNewLayout,
    newDesignLayoutDefault,
    onClickAddCustomLayout,
  };
};

const NoCustomLayoutCTA = ({ children }) => {
  const LANG = useTranslatedText();
  return (
    <EmptyStateActionWrapper
      title={LANG.noCustomListConfiguration}
      description={
        <Typography>
          {LANG.noCustomListConfigurationDescription}
          <EmphasisText>{LANG.noCustomListConfigurationCTA}</EmphasisText>
        </Typography>
      }
      illustrationGraphic={<Image src="/img/list.png" />}
    >
      {children}
    </EmptyStateActionWrapper>
  );
};
